/**
 * Annotation API (shared)
 *
 * Create and list Dynamics CRM annotations (notes).
 * Used by both Vera Ops and Vera Sales.
 */

import { dataverseClient, TABLES, type QueryOptions } from '@shared/api';
import { assertGuid } from '@shared/lib';
import type { Annotation, CreateNoteInput } from '../model/types';
import { encodeNoteSubject } from '../lib/noteMetadata';

// =============================================================================
// Entity Interface
// =============================================================================

interface AnnotationEntity {
  annotationid: string;
  subject?: string;
  notetext?: string;
  _objectid_value?: string;
  objecttypecode?: string;
  createdon?: string;
  modifiedon?: string;
  _createdby_value?: string;
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

const SELECT_FIELDS = [
  'annotationid', 'subject', 'notetext',
  '_objectid_value', 'objecttypecode',
  'createdon', 'modifiedon', '_createdby_value',
];

function mapToDomain(entity: AnnotationEntity): Annotation {
  return {
    id: entity.annotationid,
    subject: entity.subject ?? undefined,
    noteText: entity.notetext ?? undefined,
    objectId: entity._objectid_value ?? undefined,
    objectTypeCode: entity.objecttypecode ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
    createdById: entity._createdby_value ?? undefined,
    createdByName:
      (entity['_createdby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
  };
}

// =============================================================================
// API Functions
// =============================================================================

async function listForRecord(objectId: string): Promise<Annotation[]> {
  const options: QueryOptions = {
    select: SELECT_FIELDS,
    filter: `_objectid_value eq '${assertGuid(objectId, 'objectId')}'`,
    orderBy: 'createdon desc',
    top: 100,
  };
  const response = await dataverseClient.list<AnnotationEntity>(TABLES.ANNOTATION, options);
  return response.value.map(mapToDomain);
}

async function createNote(input: CreateNoteInput): Promise<Annotation> {
  const subject = encodeNoteSubject(input.noteType, input.tags, input.userSubject);
  const payload: Record<string, unknown> = {
    subject,
    notetext: input.content,
    'objectid_account@odata.bind': `/accounts(${assertGuid(input.accountId, 'accountId')})`,
  };
  const entity = await dataverseClient.create<AnnotationEntity>(TABLES.ANNOTATION, payload);
  return mapToDomain(entity);
}

// =============================================================================
// Export
// =============================================================================

export const annotationApi = {
  listForRecord,
  createNote,
};
